/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/


import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import userIcon from "dashboard-template/assets/images/userIcon.png";
import { HeaderEnum, PersonInterface } from "@malmur-technologies/medicalsystem-shared";
import Typography from "components/generals/Typography";
import { calculateAge } from "utils/generic.utils";
import Box from "components/generals/Box";
import useHttp from "hooks/http.hook";
import { useEffect, useState, useMemo, useCallback } from "react";
import { CoreAPI_URLs } from "constants/http.const";
import { Stack, CircularProgress, Box as MuiBox, IconButton, Tooltip } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import Avatar from "components/generals/Avatar";
import { useTranslation } from "react-i18next";
import { useAuthenticatedAsset } from "hooks/useAuthenticatedAsset.hook";
import { PrintPatientRegistrationCard } from "../templates/PrintPatientRegistrationCardTemplate";
import "styles/components/_general.scss";

/**
 * Properties for the HeaderPersonInformation component.
 * 
 * @interface HeaderProps
 * @property {Partial<PersonInterface>} [personInformation] - Person data to display
 * @property {boolean} [progressNote] - Whether displayed in progress note context (hides photo)
 */
interface HeaderProps {
  personInformation?: Partial<PersonInterface>;
  progressNote?: boolean;
  showPhoto?: boolean;
}

/**
 * Person information header card component.
 * Displays person demographics with profile photo.
 * 
 * Features:
 * - Profile photo with authenticated blob loading
 * - Loading indicator with progress for photo
 * - Fallback to default user icon
 * - Person name and contact information
 * - Date of birth and calculated age
 * - Formatted phone numbers
 * - Address details (street, city, state, ZIP)
 * - Responsive grid layout
 * - Conditional photo display (hidden in progress notes)
 * - Automatic person data refresh via API
 * 
 * Photo Loading:
 * - Uses useAuthenticatedAsset hook for secure blob access
 * - Shows CircularProgress during load
 * - Displays load progress percentage
 * - Falls back to default icon on error
 * 
 * Data Display:
 * - Name: Full name from person object
 * - Age: Calculated from date of birth
 * - DOB: Formatted as YYYY-MM-DD
 * - Phone: Primary phone number
 * - Address: Complete address with city, state, ZIP
 * 
 * Layout Modes:
 * - Standard: Shows photo + demographics
 * - Progress Note: Demographics only (progressNote=true)
 * 
 * @param {HeaderProps} props - Component properties
 * @param {Partial<PersonInterface>} props.personInformation - Person data
 * @param {boolean} props.progressNote - Hide photo if true
 * 
 * @returns {JSX.Element} Person information header card
 */
function HeaderPersonInformation({
  personInformation,
  progressNote,
  showPhoto
}: Readonly<HeaderProps>): JSX.Element {
  const [_isLoading, _error, sendRequest] = useHttp();
  const [personModel, setPersonModel] = useState<Partial<PersonInterface>>();
  const { t } = useTranslation();
  const sex = personModel?.sex ? (personModel?.sex === "MALE" ? t("headerPersonInformation.male") : t("headerPersonInformation.female")) : t("headerPersonInformation.noRegisteredSex");
  // Use authenticated asset hook for profile photo
  const { blobUrl: photoUrl, isLoading: photoLoading, progress } = useAuthenticatedAsset(
    personInformation?.photo
  );

  const formattedDate = useMemo(() => personModel?.dob?.split("T")[0], [personModel?.dob]);
  const age = useMemo(() => calculateAge(personModel?.dob), [personModel?.dob]);

  useEffect(() => {
    setPersonModel(personInformation);
    if (personInformation && personInformation.id !== '00000000-0000-0000-0000-000000000000') {
      sendRequest(CoreAPI_URLs.PERSONS_GENERAL_OPERATIONS, onGetPerson, "GET", undefined, [
        {
          header: HeaderEnum.RESOURCE_ID,
          value: personInformation.id,
        },
      ]);
    }
  }, [personInformation]);

  const onGetPerson = (person: PersonInterface) => {
    if (person) {
      setPersonModel(person);
    } else {
      setPersonModel(null);
    }
  };

  /**
   * Handles printing the patient registration card.
   * Opens a new window with the formatted registration card and triggers print dialog.
   */
  const handlePrintRegistrationCard = useCallback(async () => {
    if (!personModel) return;

    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const printWindowWidth = 700;
    const printWindowHeight = 600;
    const left = screenWidth / 2 - printWindowWidth / 2;
    const top = screenHeight / 2 - printWindowHeight / 2;

    const htmlContent = PrintPatientRegistrationCard({
      personModel,
      age: age || 0,
      t
    });

    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(
      url,
      "",
      `width=${printWindowWidth},height=${printWindowHeight},left=${left},top=${top}`
    );

    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
        URL.revokeObjectURL(url);
      };
    }
  }, [personModel, age, t, photoUrl]);

  return (
    <Card id="profile" className={`card-base header-person-card`} >
      {personModel && !progressNote && (
        <Tooltip title={t("headerPersonInformation.printRegistrationCard")}>
          <IconButton
            onClick={handlePrintRegistrationCard}
            className="print-button"
            size="large"
          >
            <PrintIcon color="primary" />
          </IconButton>
        </Tooltip>
      )}
      <Box>
        <Grid container spacing={3}>
          {!progressNote && showPhoto && (
            <Grid item>
              <Stack direction="row" spacing={2}>
                {photoLoading ? (
                  <MuiBox className="photoLoadingContainer">
                    <MuiBox className="progressContainer">
                      <CircularProgress
                        variant="determinate"
                        value={progress}
                        size={60}
                        thickness={4}
                      />
                      <MuiBox className="progressLabel">
                        <Typography variant="caption" color="secondary">
                          {`${progress}%`}
                        </Typography>
                      </MuiBox>
                    </MuiBox>
                  </MuiBox>
                ) : (
                  <Avatar
                    src={photoUrl || userIcon}
                    alt={personInformation.photo?.orgName || "profile-image"}
                    className="patient-avatar"
                  />
                )}
              </Stack>
            </Grid>
          )}
          <Grid item>
            <Box height="100%" mt={0.5} lineHeight={1}>
              {personModel ? (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h5" className="title-secondary" fontWeight="medium">
                      {personModel.name} {personModel.lastname}
                    </Typography>
                  </Grid>
                  {personModel.patient && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text" fontWeight="bold">
                        {t("headerPersonInformation.accountNumber")}:{" "}
                        {personModel.patient.accountNumber}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text" fontWeight="bold">
                      {t("headerPersonInformation.age")}: {age ? `${age} ${t("headerPersonInformation.years")}` : t("headerPersonInformation.noRegisteredAge")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text" fontWeight="bold">
                      {t("headerPersonInformation.sex")}: {sex || t("headerPersonInformation.noRegisteredSex")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text" fontWeight="bold">
                      {t("headerPersonInformation.birthDate")}: {formattedDate || t("headerPersonInformation.noRegisteredBirthDate")}
                    </Typography>
                  </Grid>
                  {!personModel.address && !personModel.city && !personModel.state && !personModel.country && !personModel.zip ? (
                    <Grid item xs={12}>
                      <Typography
                        variant="caption"
                        color="text"
                        fontWeight="bold"
                        className="addressText"
                      >
                        {t("headerPersonInformation.address")}: {t("headerPersonInformation.noRegisteredAddress")}
                      </Typography>
                    </Grid>
                  ) : (
                    <Grid item xs={12}>
                      <Typography
                        variant="caption"
                        color="text"
                        fontWeight="bold"
                        className="addressText"
                      >
                        {t("headerPersonInformation.address")}: {personModel.address},{" "}
                        {personModel.city}, {personModel.state}, {personModel.country},{" "}
                        {personModel.zip}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text" fontWeight="bold">
                      {t("headerPersonInformation.phone")}: {personModel.phones?.length > 0 ? personModel.phones[0].number : t("headerPersonInformation.noPhones")}
                    </Typography>
                  </Grid>
                </>
              ) : (
                <Typography variant="h5" fontWeight="medium">
                  {t("headerPersonInformation.noPersonInformation")}:
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
}
export default HeaderPersonInformation;
